/**
 * The shape of a file manifest, drawn rather than described.
 *
 * A single JSON block shows a flat list of links, which is fine until the file
 * exceeds `max_links` and the manifests start nesting. At that point the thing
 * worth seeing is the *structure*: which manifest covers which stretch of the
 * file, and how a parent's `s` is just the sum of its descendants.
 *
 * Nested boxes rather than drawn edges, because containment is exactly the
 * relationship being shown — a manifest holds these links, and those links hold
 * those, down to the chunks that hold the bytes.
 *
 * Only the root opens by default. A deep tree expanded all at once is a wall of
 * boxes that teaches nothing; the structure lands when the reader opens one
 * level at a time and finds the same pattern repeating.
 */

import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { formatBytes, toHex } from "@/hashtree/bytes";
import type { BuiltNode } from "@/hashtree/nodes";
import { cn } from "@/lib/utils";
import { HashValue } from "./HashDisplay";
import { Prop } from "./Prop";

interface Range {
  start: number;
  /** Exclusive. */
  end: number;
}

/** Byte range of the original file that each node accounts for. */
function byteRanges(root: BuiltNode): Map<string, Range> {
  const ranges = new Map<string, Range>();
  let offset = 0;

  const walk = (node: BuiltNode) => {
    const start = offset;
    if (node.kind === "blob") offset += node.plaintext.length;
    else for (const child of node.children) walk(child);
    ranges.set(node.id, { start, end: offset });
  };

  walk(root);
  return ranges;
}

/** How deep the manifest nesting goes, counting the chunk layer as 0. */
function depthOf(node: BuiltNode): number {
  if (node.kind === "blob") return 0;
  return 1 + Math.max(0, ...node.children.map(depthOf));
}

function countNodes(node: BuiltNode): number {
  return 1 + node.children.reduce((sum, child) => sum + countNodes(child), 0);
}

/** Leaf chunks beneath a node — what a collapsed branch is hiding. */
function countChunks(node: BuiltNode): number {
  if (node.kind === "blob") return 1;
  return node.children.reduce((sum, child) => sum + countChunks(child), 0);
}

function idsWithChildren(node: BuiltNode, into: string[] = []): string[] {
  if (node.children.length > 0) into.push(node.id);
  for (const child of node.children) idsWithChildren(child, into);
  return into;
}

/** Past this the diagram stops being a diagram, so children collapse to a summary. */
const CHILD_LIMIT = 12;

export function FileTreeDiagram({ root, fileName }: { root: BuiltNode; fileName: string }) {
  const ranges = useMemo(() => byteRanges(root), [root]);
  const expandable = useMemo(() => idsWithChildren(root), [root]);

  const [selected, setSelected] = useState<string | null>(root.id);
  // Only the root starts open, so a nested tree does not fill the screen.
  const [open, setOpen] = useState<Set<string>>(() => new Set([root.id]));

  const depth = depthOf(root);
  const total = countNodes(root);
  const allOpen = expandable.every((id) => open.has(id));

  const toggle = (id: string) =>
    setOpen((current) => {
      const nextOpen = new Set(current);
      if (nextOpen.has(id)) nextOpen.delete(id);
      else nextOpen.add(id);
      return nextOpen;
    });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">{total}</span> nodes
        </span>
        <span>
          nesting depth <span className="font-medium text-foreground">{depth}</span>
        </span>
        <span>
          total <span className="font-medium text-foreground">{formatBytes(root.size)}</span>
        </span>
        {depth > 1 && (
          <span className="text-amber-700 dark:text-amber-400">max_links was exceeded, so the manifests nest</span>
        )}

        {expandable.length > 1 && (
          <button
            type="button"
            onClick={() => setOpen(allOpen ? new Set([root.id]) : new Set(expandable))}
            className="ml-auto rounded border px-2 py-0.5 text-[0.7rem] transition-colors hover:bg-muted hover:text-foreground"
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        )}
      </div>

      <DiagramNode
        node={root}
        ranges={ranges}
        label={fileName}
        selected={selected}
        onSelect={setSelected}
        isOpen={(id) => open.has(id)}
        onToggle={toggle}
        fileSize={root.size}
        depth={0}
      />
    </div>
  );
}

const KIND_STYLES: Record<string, { border: string; bg: string; tint: string }> = {
  file: {
    border: "border-violet-500/40",
    bg: "bg-violet-500/5",
    tint: "bg-violet-500/40",
  },
  blob: {
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/5",
    tint: "bg-emerald-500/50",
  },
};

function DiagramNode({
  node,
  ranges,
  label,
  selected,
  onSelect,
  isOpen,
  onToggle,
  fileSize,
  depth,
}: {
  node: BuiltNode;
  ranges: Map<string, Range>;
  label?: string;
  selected: string | null;
  onSelect: (id: string) => void;
  isOpen: (id: string) => boolean;
  onToggle: (id: string) => void;
  fileSize: number;
  depth: number;
}) {
  const range = ranges.get(node.id)!;
  const style = KIND_STYLES[node.kind === "blob" ? "blob" : "file"]!;
  const isSelected = selected === node.id;
  const isChunk = node.kind === "blob";
  const hasChildren = node.children.length > 0;
  const expanded = hasChildren && isOpen(node.id);

  const shown = node.children.slice(0, CHILD_LIMIT);
  const hidden = node.children.length - shown.length;
  const childrenAreManifests = node.children.some((child) => child.kind !== "blob");

  return (
    <div
      className={cn(
        "rounded-lg border transition-colors",
        style.border,
        style.bg,
        isSelected && "ring-2 ring-primary/40",
      )}
    >
      <div className="flex items-center gap-1 px-1.5 py-1.5">
        <button
          type="button"
          onClick={() => onToggle(node.id)}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse this node" : "Expand this node"}
          className={cn(
            "shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            !hasChildren && "invisible",
          )}
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>

        <button
          type="button"
          onClick={() => onSelect(node.id)}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-left"
        >
          <Prop name="t" value={isChunk ? 0 : 1} className="text-[0.7rem]" />
          <span className="text-xs font-medium">{label ?? (isChunk ? "chunk" : "file manifest")}</span>
          <span className="text-[0.7rem] text-muted-foreground">
            bytes {range.start.toLocaleString()}–{Math.max(range.start, range.end - 1).toLocaleString()}
          </span>
          <span className="ml-auto pr-1 text-[0.7rem] tabular-nums text-muted-foreground">
            <Prop name="s" className="text-[0.7rem]" />
            {"="}
            {node.size.toLocaleString()}
          </span>
        </button>
      </div>

      {/* Where this node sits in the file, drawn to scale. */}
      <div className="mx-2.5 mb-1.5 h-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full", style.tint)}
          style={{
            marginLeft: `${(range.start / Math.max(fileSize, 1)) * 100}%`,
            width: `${Math.max(((range.end - range.start) / Math.max(fileSize, 1)) * 100, 0.5)}%`,
          }}
        />
      </div>

      {isSelected && (
        <div className="mx-2.5 mb-2 space-y-1 rounded border bg-background/70 px-2 py-1.5">
          <FieldLine name="h" value={toHex(node.encrypted.blobHash)} tone="hash" />
          <FieldLine name="k" value={toHex(node.encrypted.chkKey)} tone="key" />
        </div>
      )}

      {/* Collapsed: say what is inside, so the branch is worth opening. */}
      {hasChildren && !expanded && (
        <button
          type="button"
          onClick={() => onToggle(node.id)}
          className="w-full border-t border-dashed px-2.5 py-1.5 text-left text-[0.65rem] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          {node.children.length} {childrenAreManifests ? "child manifests" : "chunks"}
          {childrenAreManifests && <> holding {countChunks(node).toLocaleString()} chunks</>} — click to expand
        </button>
      )}

      {expanded && (
        <div className="space-y-1.5 border-t border-dashed px-2.5 py-2">
          <p className="text-[0.65rem] text-muted-foreground">
            {node.children.length} link{node.children.length === 1 ? "" : "s"}, in byte order
            {depth === 0 && childrenAreManifests && <> — each one is another manifest, not a chunk</>}
          </p>
          {shown.map((child, index) => (
            <div key={child.id} className="flex gap-1.5">
              <span className="w-4 shrink-0 pt-1.5 text-right font-mono text-[0.6rem] text-muted-foreground">
                {index}
              </span>
              <div className="min-w-0 flex-1">
                <DiagramNode
                  node={child}
                  ranges={ranges}
                  selected={selected}
                  onSelect={onSelect}
                  isOpen={isOpen}
                  onToggle={onToggle}
                  fileSize={fileSize}
                  depth={depth + 1}
                />
              </div>
            </div>
          ))}
          {hidden > 0 && (
            <p className="pl-6 text-[0.65rem] text-muted-foreground">
              + {hidden} more link{hidden === 1 ? "" : "s"} at this level
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function FieldLine({ name, value, tone }: { name: string; value: string; tone: "hash" | "key" }) {
  return (
    <div className="flex items-start gap-2">
      <Prop name={name} className="shrink-0 pt-px text-[0.7rem]" />
      <HashValue value={value} tone={tone} className="min-w-0 flex-1 text-[0.65rem]" />
    </div>
  );
}

/**
 * The size arithmetic, shown rather than asserted.
 *
 * BUD-17 says a parent link's `s` is the sum of its descendants, and a reader
 * relies on that to know a file's length from the root alone.
 */
export function SizeSum({ node }: { node: BuiltNode }) {
  if (node.children.length === 0) return null;

  const parts = node.children.map((child) => child.size);
  const sum = parts.reduce((total, size) => total + size, 0);

  return (
    <p className="font-mono text-xs text-muted-foreground">
      {parts.slice(0, 8).join(" + ")}
      {parts.length > 8 && ` + … (${parts.length - 8} more)`} = <span className="text-foreground">{sum}</span> bytes
    </p>
  );
}
